import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { TabFilterProps } from './tab-filter.component';

const resolveInitialActive = (props: TabFilterProps, searchParams: URLSearchParams): string => {
    if (props.useQueryParams) {
        const fromUrl = searchParams.get(props.useQueryParams.key);
        if (fromUrl && props.keys.includes(fromUrl)) {
            return fromUrl;
        }
    }
    return props.defaultActive ?? props.keys[0];
};

export const useTabFilter = (props: TabFilterProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const [active, setActive] = useState<string>(() => resolveInitialActive(props, searchParams));

    useEffect(() => {
        if (!props.useQueryParams) return;
        if (!searchParams.has(props.useQueryParams.key)) return;
        const fromUrl = searchParams.get(props.useQueryParams.key)!;
        if (!props.keys.includes(fromUrl)) return;
        setActive(fromUrl);
        props.onChange?.(fromUrl);
    }, []);

    useEffect(() => {
        if (!props.useQueryParams) return;

        const { key } = props.useQueryParams;
        setSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                params.set(key, active);

                if (props.filterKeys) {
                    props.filterKeys.forEach((filterKey) => {
                        params.delete(filterKey);
                    });
                }

                // Avoid redundant navigations that can trigger unnecessary rerenders.
                if (params.toString() === prev.toString()) {
                    return prev;
                }

                return params;
            },
            { state: location.state ?? undefined }
        );
    }, [active]);

    return {
        active,
        setActive
    };
};
